import ugcApi from "@apis/ugc.api";
import CommonUtils from "../utils/common.utils";
import { FileResponse, FileStatus } from "@plugins/file-uploader-v2/types.ts";

export default class UgcFileService {
  constructor(config) {
    this.getContext = config.getContext;
  }

  async create(fileResponse: FileResponse) {
    const context = await this.getContext();
    const requestData = {
      ...context,
      fileEntry: {
        fileUuid: fileResponse.uuid,
        filename: fileResponse.name,
      },
    };
    const response = await ugcApi.createInlineUgc(requestData);
    if (response.code === "error") {
      throw response;
    }
    return response.data;
  }

  async saveMediaFile(mediaUuid, fileResponse) {
    const requestData = {
      fileUuid: fileResponse.uuid,
      filename: fileResponse.name,
    };
    const response = await ugcApi.saveInlineUgc(mediaUuid, requestData);
    if (response.code === "error") {
      throw response.error;
    }
    return response.data;
  }

  /**
   * Create a UGC placeholder record before the file is uploaded.
   * This ensures resourceType + resourceUuid are available in Valkey
   * at generate-scan-url time so FileMoveJobListenerImpl can route
   * the file move after the scan completes.
   */
  async preCreate() {
    const context = await this.getContext();
    context.fileName = "recording.mp3";
    const response = await ugcApi.preCreateInlineUgc(context);
    if (response.code === "error") {
      throw response;
    }
    return response.data;
  }

  /**
   * Function to retrieve and return UGC's meta data when the file is ready.
   * Retry is required as meta data may not be complete while the media file is still processing.
   * @param uuid
   * @returns {Promise}
   */
  async getMetaDataWhenFileReady(uuid) {
    const maxRetries = 20;
    const retryInterval = 3000;
    return CommonUtils.retryPromise(
      uuid,
      this.getMetaDataWhenFileReadyApi,
      maxRetries,
      retryInterval,
    );
  }

  async getMetaDataWhenFileReadyApi(uuid) {
    const response = await ugcApi.getMetaData({ uuid });
    if (response.code !== "ok") {
      throw new Error("Response not ok");
    }
    const metaData = response.data;
    if (metaData.fileStatus?.toUpperCase() !== FileStatus.READY) {
      throw new Error("File not ready");
    }
    return metaData;
  }

  getType() {
    return "ugc";
  }
}
